/**
 * @file Router Class
 * @description Handles page-based routing, feature detection, and lazy loading
 */

/**
 * Router Class
 * Manages page routing and feature detection
 */
export class Router {
  constructor() {
    this.currentRoute = null;
    this.routes = new Map();
    this.features = new Map();
    this.isInitialized = false;
  }

  /**
   * Initialize the router
   */
  async init() {
    try {
      console.log("🛣️  Initializing Router...");

      // Setup routes
      this.setupRoutes();

      // Setup features
      this.setupFeatures();

      // Detect current route
      this.currentRoute = this.detectCurrentRoute();

      this.isInitialized = true;
      console.log("✅ Router initialized");
    } catch (error) {
      console.error("❌ Router initialization failed:", error);
      throw error;
    }
  }

  /**
   * Setup route definitions
   */
  setupRoutes() {
    // Industry routes
    this.routes.set("industry-single", {
      pattern: /\/industry\/[^\/]+$/,
      feature: "industry-single",
      title: "Industry Detail",
    });

    this.routes.set("industry-archive", {
      pattern: /\/industry\/?$/,
      feature: "industry-archive",
      title: "Industry Archive",
    });

    // FDI routes
    this.routes.set("fdi-archive", {
      pattern: /\/fdi\/?$/,
      feature: "fdi-archive",
      title: "FDI Archive",
    });

    // Account routes
    this.routes.set("account", {
      pattern: /\/account\/?/,
      feature: "account",
      title: "Account",
    });

    // Home route
    this.routes.set("home", {
      pattern: /^\/?$/,
      feature: "home",
      title: "Home",
    });
  }

  /**
   * Setup feature definitions
   */
  setupFeatures() {
    // Industry features
    this.features.set("industry-single", {
      name: "Industry Single",
      modules: ["singleLocationMap", "shareLink", "chatBubbles", "sliders"],
      description: "Industry detail page with map and information",
    });

    this.features.set("industry-archive", {
      name: "Industry Archive",
      modules: ["locationList", "regionMap", "chatBubbles", "sliders"],
      description: "Industry listing page with map and filters",
    });

    // FDI features
    this.features.set("fdi-archive", {
      name: "FDI Archive",
      modules: ["fdiMap", "chatBubbles", "sliders"],
      description: "FDI locations page with interactive map",
    });

    // Account features
    this.features.set("account", {
      name: "Account",
      modules: ["accountMenu", "contentToggle", "routeStepToggle", "collection", "chatBubbles"],
      description: "User account and collection management",
    });

    // Home features
    this.features.set("home", {
      name: "Home",
      modules: ["chatBubbles", "sliders"],
      description: "Homepage with basic components",
    });
  }

  /**
   * Detect current route based on URL and page elements
   * @returns {string} Current route identifier
   */
  detectCurrentRoute() {
    const path = window.location.pathname;

    // Check for specific page elements first
    if (document.querySelector(".single-industry")) {
      return "industry-single";
    }

    if (document.querySelector(".archive-industry")) {
      return "industry-archive";
    }

    if (document.querySelector(".archive-fdi")) {
      return "fdi-archive";
    }

    if (document.querySelector(".page-account")) {
      return "account";
    }

    // Fallback to URL pattern matching
    for (const [routeName, route] of this.routes) {
      if (route.pattern.test(path)) {
        return routeName;
      }
    }

    // Default to home
    return "home";
  }

  /**
   * Get current feature
   * @returns {string} Current feature identifier
   */
  getCurrentFeature() {
    const route = this.routes.get(this.currentRoute);
    return route ? route.feature : "default";
  }

  /**
   * Get current route info
   * @returns {Object} Current route information
   */
  getCurrentRouteInfo() {
    const route = this.routes.get(this.currentRoute);
    return route || { feature: "default", title: "Unknown" };
  }

  /**
   * Get feature info
   * @param {string} featureName - Feature name
   * @returns {Object} Feature information
   */
  getFeatureInfo(featureName) {
    return (
      this.features.get(featureName) || {
        name: "Unknown",
        modules: [],
        description: "Unknown feature",
      }
    );
  }

  /**
   * Check if current page matches a route
   * @param {string} routeName - Route name to check
   * @returns {boolean} True if current page matches the route
   */
  isCurrentRoute(routeName) {
    return this.currentRoute === routeName;
  }

  /**
   * Check if current page has a specific feature
   * @param {string} featureName - Feature name to check
   * @returns {boolean} True if current page has the feature
   */
  hasFeature(featureName) {
    return this.getCurrentFeature() === featureName;
  }

  /**
   * Get all available routes
   * @returns {Array} Array of route names
   */
  getAvailableRoutes() {
    return Array.from(this.routes.keys());
  }

  /**
   * Get all available features
   * @returns {Array} Array of feature names
   */
  getAvailableFeatures() {
    return Array.from(this.features.keys());
  }

  /**
   * Navigate to a new route (for future SPA functionality)
   * @param {string} routeName - Route to navigate to
   * @param {Object} options - Navigation options
   */
  async navigate(routeName, options = {}) {
    if (!this.routes.has(routeName)) {
      throw new Error(`Route '${routeName}' not found`);
    }

    const route = this.routes.get(routeName);

    // For now, just update the current route
    // In the future, this could handle actual navigation
    this.currentRoute = routeName;

    // Emit navigation event
    window.dispatchEvent(
      new CustomEvent("router:navigate", {
        detail: { route: routeName, feature: route.feature },
      }),
    );
  }

  /**
   * Destroy the router
   */
  destroy() {
    this.routes.clear();
    this.features.clear();
    this.currentRoute = null;
    this.isInitialized = false;
    console.log("🔄 Router destroyed");
  }
}
